import { AuthStorage } from "utils/auth-storage";
import { useNavigate } from "react-router-dom";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ProjectServices } from "apis/Services/ProjectService";
import { setUnsavedChangesModal } from "redux/project-reducer";
import { Select, Modal, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import styles from "./nav.module.css";

const EMPTY_PROJECTS = { content: [] };

const normalizeCompanyId = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? value : numericValue;
};

const getCompaniesIdsObject = () => {
  try {
    const parsed = JSON.parse(AuthStorage.getItem("companies_object") || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch (error) {
    console.error("Unable to read the cached company list", error);
    return {};
  }
};

const getCachedProjects = (companyId) => {
  if (!companyId) return EMPTY_PROJECTS;
  const cacheKey = `projects_${companyId}`;
  try {
    const cachedProjects = window.localStorage.getItem(cacheKey);
    if (!cachedProjects) return EMPTY_PROJECTS;
    const parsed = JSON.parse(cachedProjects);
    if (Array.isArray(parsed?.content)) return parsed;
    window.localStorage.removeItem(cacheKey);
  } catch (error) {
    console.error("Unable to read the cached project list", error);
    window.localStorage.removeItem(cacheKey);
  }
  return EMPTY_PROJECTS;
};

const getErrorMessage = () => "Unable to load projects. Please try again.";

const ProjectSwitcher = ({ companyId, projectId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [projectsContent, setProjectsContent] = useState(EMPTY_PROJECTS);
  const [projectsError, setProjectsError] = useState("");
  const { unsavedChanges } = useSelector((state) => state.projectDetails);
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const selectRef = useRef(null);
  const requestVersion = useRef(0);

  const [isModalVisible, setIsModalVisible] = useState(false);
  // State to control the Ant Design Select dropdown's open state
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const loadProjects = useCallback(async () => {
    const version = ++requestVersion.current;
    const cachedProjects = getCachedProjects(companyId);
    setProjectsContent(cachedProjects);
    setProjectsError("");

    if (!companyId) {
      setProjectsError("No company is selected.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await ProjectServices.getProjects({
        company_id: companyId,
        code: AuthStorage.getItem("token"),
        loginEmail: AuthStorage.getItem("email"),
      });
      if (version !== requestVersion.current) return;
      if (!Array.isArray(response?.content)) {
        throw new Error("The project list response is invalid.");
      }
      setProjectsContent(response);
      window.localStorage.setItem(
        `projects_${companyId}`,
        JSON.stringify(response),
      );
    } catch (error) {
      if (version === requestVersion.current) {
        setProjectsError(getErrorMessage(error));
      }
    } finally {
      if (version === requestVersion.current) setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadProjects();
    return () => {
      requestVersion.current += 1;
    };
  }, [loadProjects]);

  const handleSelectChange = (value) => {
    if (unsavedChanges) {
      dispatch(
        setUnsavedChangesModal(
          `/main/company/${companyId}/projects/${value}/dashboard`
        )
      );
      // Close the dropdown when unsaved changes modal is shown
      setIsSelectOpen(false);
      return;
    } else {
      if (value) {
        window.location.href = `/main/company/${companyId}/projects/${value}/dashboard`;
      }
    }
  };

  const handleDivClick = (e) => {
    if (!e.target.closest(".ant-select-selector") && !isModalVisible) {
      setIsSelectOpen((prev) => !prev);
    }
  };

  const renderOptions = () => {
    return projectsContent.content
      .filter(
        (item) =>
          item &&
          typeof item === "object" &&
          item["Project ID"] !== null &&
          item["Project ID"] !== undefined,
      )
      .map((item) => (
        <Select.Option
          key={item["Project ID"]}
          value={item["Project ID"]}
          label={`${item["Project Number"] || ""} - ${item["Project Name"] || ""}`}
        >
          {item["Project Number"] || "—"} - {item["Project Name"] || "Unnamed project"}
        </Select.Option>
      ));
  };

  const [selectedCompanyIdIndex, setSelectedCompanyIdIndex] = useState(
    () =>
      normalizeCompanyId(AuthStorage.getItem("company_id")) ??
      normalizeCompanyId(companyId),
  );

  const companiesIdsObject = useMemo(getCompaniesIdsObject, [companyId]);

  const currentCompanyName = Object.keys(companiesIdsObject).find(
    (key) =>
      String(companiesIdsObject[key]) === String(selectedCompanyIdIndex),
  );

  const selectOptions = Object.keys(companiesIdsObject)
    .map((name) => ({
      value: normalizeCompanyId(companiesIdsObject[name]),
      label: name,
    }))
    .filter(({ value }) => value !== undefined);

  useEffect(() => {
    const storedCompanyId = normalizeCompanyId(
      AuthStorage.getItem("company_id"),
    );
    if (
      selectedCompanyIdIndex !== undefined &&
      String(selectedCompanyIdIndex) !== String(storedCompanyId)
    ) {
      window.localStorage.removeItem(`projects_${selectedCompanyIdIndex}`);
      navigate(`/main/company/${selectedCompanyIdIndex}/projects`);
      AuthStorage.setItem("company_id", selectedCompanyIdIndex);
      setTimeout(() => {
        window.location.reload();
      }, 200);
    }
  }, [navigate, selectedCompanyIdIndex]);

  const handleCompanySelect = (selectedOption) => {
    setSelectedCompanyIdIndex(selectedOption);
  };

  return (
    <div
      className={`${styles.switcher} ${isSelectOpen ? styles.switcherOpen : ""}`}
      onClick={handleDivClick}
    >
      <div className={styles.contextContent}>
        <div className={styles.companyName} title={currentCompanyName}>
          {currentCompanyName || "Company unavailable"}
        </div>
        <Select
          ref={selectRef}
          onChange={handleSelectChange}
          open={isSelectOpen}
          onDropdownVisibleChange={(visible) => setIsSelectOpen(visible)}
          showSearch
          aria-label="Switch project"
          className={styles.projectSelect}
          optionFilterProp="label"
          variant="borderless"
          size="small"
          value={projectId || null}
          loading={isLoading}
          popupMatchSelectWidth={300}
          placeholder={projectsError ? "Projects unavailable" : "Select a Project"}
          notFoundContent={
            isLoading
              ? "Loading projects…"
              : projectsError || "No projects available"
          }
          filterOption={(input, option) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
          popupRender={(menu) => (
            <>
              {menu}
              {projectsError && (
                <div
                  className={styles.dropdownError}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <span>{projectsError}</span>
                  <Button type="link" size="small" onClick={loadProjects}>
                    Retry
                  </Button>
                </div>
              )}
              <div
                className={styles.dropdownFooter}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div>
                  <div className={styles.dropdownLabel}>
                    Current Company
                  </div>
                  <div>{currentCompanyName || "Unavailable"}</div>
                </div>
                <Button
                  type="text"
                  onClick={(event) => {
                    event.stopPropagation();
                    setIsModalVisible(true);
                    setIsSelectOpen(false);
                  }}
                  className={styles.changeButton}
                  disabled={!selectOptions.length}
                >
                  Change
                </Button>
              </div>
            </>
          )}
        >
          {renderOptions()}
        </Select>
      </div>

      <Modal
        title="Change Company"
        open={isModalVisible}
        centered
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={360}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={styles.companyPicker}
          onClick={(e) => e.stopPropagation()}
        >
          <Select
            style={{ width: "100%" }}
            aria-label="Change company"
            options={selectOptions}
            value={selectedCompanyIdIndex}
            onChange={handleCompanySelect}
            placeholder={"select a company"}
            showSearch
            optionFilterProp="label"
            notFoundContent="No companies available"
          />
        </div>
      </Modal>
    </div>
  );
};

export default ProjectSwitcher;
